import { PAGE_SIZE_QUERY_PARAM } from '@/shared/components/ui/data-table/hooks/use-filter-params/use-filter-params.constants';
import { useFilterPreferencesConfig } from '@/shared/features/filter-preferences/context/filter-preferences-config-context';
import { TFilterPreferences } from '@/shared/features/filter-preferences/hooks/use-filter-preferences/use-filter-preferences.types';
import { useUiPreferencesWriter } from '@/shared/features/filter-preferences/hooks/use-ui-preferences-writer/use-ui-preferences-writer';
import {
  hasLayerDefault,
  resolveFilterDefault,
} from '@/shared/features/filter-preferences/utils/resolve-filter-defaults/resolve-filter-defaults';
import { TFilterValuePredicate } from '@/shared/features/filter-preferences/utils/resolve-filter-defaults/resolve-filter-defaults.types';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useReadUserUiPreferencesQuery } from '@/shared/redux/api/ident/ident-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';
import { TUserUiPreferences } from '@/shared/types/ui-preferences.types';

export const useFilterPreferences = (): TFilterPreferences => {
  const writer = useUiPreferencesWriter();
  const { useCompanyDefaults, companyDefaultAction } =
    useFilterPreferencesConfig();
  const { can } = usePermissionCheck();

  const user = useAppSelector(selectUser);
  const isAuthenticated = !!user;
  const tenantId = user?.attributes?.['custom:tenantId'] ?? '';
  const { data, isSuccess, isError } = useReadUserUiPreferencesQuery(
    undefined,
    {
      skip: !isAuthenticated,
    },
  );
  // Ready = settled: a failed read must fall through to code defaults, never
  // hang a list that gates on isReady.
  const userReady = !isAuthenticated || isSuccess || isError;
  const { filterDefaults: companyDefaults, isReady: companyReady } =
    useCompanyDefaults();

  // The user blob is shared across tenants, so read only the current tenant's slice.
  const userDefaults = (
    data?.readUserUiPreferences?.uiPreferences as TUserUiPreferences | undefined
  )?.filterDefaults?.[tenantId];

  const resolveDefault = (
    scopeKey: string,
    param: string,
    codeDefault?: string,
    isAllowed?: TFilterValuePredicate,
  ) =>
    resolveFilterDefault(
      scopeKey,
      param,
      codeDefault,
      { user: userDefaults, company: companyDefaults },
      isAllowed,
    );

  const hasUserDefault = (scopeKey: string, param: string) =>
    hasLayerDefault(userDefaults, scopeKey, param);

  const hasCompanyDefault = (scopeKey: string, param: string) =>
    hasLayerDefault(companyDefaults, scopeKey, param);

  // A saved view counts here too: whatever supplies a param, the scope seeded it
  // and the filter must not seed its code default over the top.
  const hasStoredDefault = (
    scopeKey: string,
    param: string,
    isAllowed?: TFilterValuePredicate,
  ) => resolveDefault(scopeKey, param, undefined, isAllowed) !== undefined;

  // Page size is a plain query param, so seeding it with the filters is all it takes.
  const getStoredDefaults = (
    scopeKey: string,
    isAllowed?: TFilterValuePredicate,
  ): Record<string, string> => {
    const allowedOnly = (filters: Record<string, string> = {}) =>
      Object.fromEntries(
        Object.entries(filters).filter(
          ([param, value]) => !isAllowed || isAllowed(param, value),
        ),
      );
    const scope = userDefaults?.[scopeKey];
    const pageSize =
      scope?.pageSize ??
      scope?.view?.pageSize ??
      companyDefaults?.[scopeKey]?.pageSize;

    return {
      ...allowedOnly(companyDefaults?.[scopeKey]?.filters),
      ...allowedOnly(scope?.view?.filters),
      ...allowedOnly(scope?.filters),
      ...(pageSize ? { [PAGE_SIZE_QUERY_PARAM]: String(pageSize) } : {}),
    };
  };

  // Column layout has no company layer -- it is personal by design.
  const getStoredColumns = (scopeKey: string) =>
    userDefaults?.[scopeKey]?.view?.columns;

  const hasStoredView = (scopeKey: string) => !!userDefaults?.[scopeKey]?.view;

  const hasAnyStoredView = () =>
    Object.values(userDefaults ?? {}).some(({ view }) => !!view);

  return {
    ...writer,
    resolveDefault,
    hasUserDefault,
    hasCompanyDefault,
    hasStoredDefault,
    getStoredDefaults,
    getStoredColumns,
    hasStoredView,
    hasAnyStoredView,
    canSetCompanyDefault: !!companyDefaultAction && can(companyDefaultAction),
    isReady: userReady && companyReady,
  };
};
